import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Monitor,
  ScanBarcode,
  Printer,
  Smartphone,
  Coins,
  Fingerprint,
  ChevronRight,
  ArrowRight,
  Layers,
  Sparkles,
  ExternalLink,
} from 'lucide-react';
import { CategoryEntity } from '../../../domain/entities/category.entity';
import { Button } from '../ui/button';

interface MegaMenuProps {
  isOpen: boolean;
  onClose: () => void;
  categories: CategoryEntity[];
  triggerRef?: React.RefObject<HTMLElement | null>;
}

export const MegaMenu: React.FC<MegaMenuProps> = ({ isOpen, onClose, categories, triggerRef }) => {
  const [selectedCategorySlug, setSelectedCategorySlug] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const activeCategorySlug =
    selectedCategorySlug && categories.some((c) => c.slug === selectedCategorySlug)
      ? selectedCategorySlug
      : categories[0]?.slug || 'pos-all-in-one';

  // Cerrar al presionar Escape o clic fuera
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (triggerRef?.current && triggerRef.current.contains(e.target as Node)) {
        return;
      }
      if (menuRef.current && !menuRef.current.contains(e.target as Node) && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  if (!isOpen) return null;

  if (categories.length === 0) {
    return (
      <div
        ref={menuRef}
        className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[94vw] max-w-xl bg-white/95 dark:bg-neutral-900/95 backdrop-blur-2xl rounded-2xl border border-neutral-200/90 dark:border-neutral-800/90 shadow-2xl p-6 z-50 animate-in fade-in-0 zoom-in-95 duration-200 text-center"
      >
        <div className="flex flex-col items-center justify-center gap-3 py-4">
          <div className="w-12 h-12 rounded-2xl bg-brand-primary/10 text-brand-primary flex items-center justify-center">
            <Layers className="w-6 h-6 animate-pulse" />
          </div>
          <h3 className="text-base font-bold text-neutral-900 dark:text-white">
            Catálogo de Hardware & Equipamiento POS
          </h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 max-w-md">
            Explora nuestra gama de terminales POS All-in-One, lectores de código de barras, impresoras térmicas y colectores móviles.
          </p>
          <div className="flex items-center gap-3 mt-2">
            <Link
              to="/catalogo"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-brand-primary text-white text-xs font-bold hover:bg-brand-primary/90 transition-colors shadow-sm inline-flex items-center gap-2"
            >
              <span>Ver Todo el Catálogo</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link
              to="/contacto"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 text-xs font-semibold hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
            >
              Asesoría Técnica
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const activeCategory = categories.find((c) => c.slug === activeCategorySlug) || categories[0];
  const subcategoriesList = activeCategory?.subcategories || [];

  const getCategoryIcon = (slug: string) => {
    switch (slug) {
      case 'pos':
      case 'pos-all-in-one':
        return Monitor;
      case 'lector-codigo-de-barras':
      case 'lectores-codigo-barras':
        return ScanBarcode;
      case 'impresoras':
      case 'impresoras-termicas':
        return Printer;
      case 'capturadores-de-datos':
      case 'capturadores-datos':
        return Smartphone;
      case 'banca':
      case 'gavetas-dinero':
        return Coins;
      case 'biometria':
      case 'biometria-acceso':
        return Fingerprint;
      case 'monitores':
        return Monitor;
      default:
        return Layers;
    }
  };

  const handleSubcategoryClick = (catSlug: string, subSlug: string) => {
    onClose();
    navigate(`/catalogo?categoria=${catSlug}&subcategoria=${subSlug}`);
  };

  const handleCategoryClick = (catSlug: string) => {
    onClose();
    navigate(`/catalogo?categoria=${catSlug}`);
  };

  return (
    <div
      ref={menuRef}
      className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[94vw] max-w-5xl bg-white/95 dark:bg-neutral-900/95 backdrop-blur-2xl rounded-2xl border border-neutral-200/90 dark:border-neutral-800/90 shadow-2xl overflow-hidden z-50 animate-in fade-in-0 zoom-in-95 duration-200"
    >
      <div className="grid grid-cols-12 min-h-[460px] max-h-[80vh]">
        {/* Panel Izquierdo: Familias Principales de Hardware */}
        <div className="col-span-4 bg-neutral-50/70 dark:bg-neutral-900/70 border-r border-neutral-200/70 dark:border-neutral-800/80 p-3 flex flex-col justify-between overflow-y-auto max-h-[80vh]">
          <div className="space-y-1">
            <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
              Familias de Hardware ({categories.length})
            </div>

            {categories.map((cat) => {
              const Icon = getCategoryIcon(cat.slug);
              const isActive = cat.slug === activeCategorySlug;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onMouseEnter={() => setSelectedCategorySlug(cat.slug)}
                  onClick={() => setSelectedCategorySlug(cat.slug)}
                  className={`w-full text-left px-3.5 py-2.5 rounded-xl text-xs font-semibold flex items-center justify-between transition-all cursor-pointer ${
                    isActive
                      ? 'bg-white dark:bg-neutral-800 text-brand-primary shadow-sm border border-neutral-200/80 dark:border-neutral-700'
                      : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800/50'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                        isActive
                          ? 'bg-brand-50 dark:bg-brand-950/50 text-brand-primary'
                          : 'bg-neutral-200/60 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="truncate">{cat.name}</span>
                  </div>

                  <ChevronRight
                    className={`w-4 h-4 flex-shrink-0 transition-transform ${
                      isActive ? 'text-brand-primary translate-x-0.5' : 'text-neutral-400 opacity-40'
                    }`}
                  />
                </button>
              );
            })}
          </div>

          {/* Enlace al Catálogo General */}
          <div className="p-2 border-t border-neutral-200/60 dark:border-neutral-800/60 mt-2">
            <Link
              to="/catalogo"
              onClick={onClose}
              className="flex items-center justify-between px-3 py-2 rounded-lg text-xs font-bold text-neutral-800 dark:text-neutral-200 hover:text-brand-primary transition-colors"
            >
              <span>Ver Catálogo Completo (Todos los Equipos)</span>
              <ArrowRight className="w-3.5 h-3.5 text-brand-primary" />
            </Link>
          </div>
        </div>

        {/* Panel Derecho: Subcategorías y Detalle Técnico */}
        <div className="col-span-8 p-6 flex flex-col justify-between overflow-y-auto max-h-[80vh]">
          <div>
            {/* Header de la Categoría Activa */}
            <div className="flex items-center justify-between pb-4 border-b border-neutral-100 dark:border-neutral-800 mb-5">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-brand-primary bg-brand-50 dark:bg-brand-950/40 px-2 py-0.5 rounded">
                    Hardware Homologado
                  </span>
                  <span className="text-xs text-neutral-400">•</span>
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">
                    {subcategoriesList.length} subfamilias disponibles
                  </span>
                </div>
                <h3 className="text-lg font-black text-neutral-900 dark:text-white tracking-tight">
                  {activeCategory.name}
                </h3>
              </div>

              <button
                type="button"
                onClick={() => handleCategoryClick(activeCategory.slug)}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-primary hover:underline group cursor-pointer"
              >
                <span>Ver toda la categoría</span>
                <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>

            {/* Grid de Subcategorías */}
            {subcategoriesList.length > 0 ? (
              <div className="grid grid-cols-2 gap-3.5">
                {subcategoriesList.map((sub) => (
                  <div
                    key={sub.id}
                    onClick={() => handleSubcategoryClick(activeCategory.slug, sub.slug)}
                    className="group p-3.5 rounded-xl border border-neutral-200/70 dark:border-neutral-800 hover:border-brand-primary/60 dark:hover:border-brand-primary/60 hover:bg-neutral-50 dark:hover:bg-neutral-800/40 transition-all cursor-pointer flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-xs font-bold text-neutral-900 dark:text-white group-hover:text-brand-primary transition-colors">
                          {sub.name}
                        </h4>
                        <ExternalLink className="w-3 h-3 text-neutral-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      {sub.description && (
                        <p className="text-[11px] text-neutral-500 dark:text-neutral-400 line-clamp-2 leading-relaxed">
                          {sub.description}
                        </p>
                      )}
                    </div>
                    <div className="mt-2.5 flex items-center gap-2">
                      <span className="text-[10px] font-semibold text-brand-primary">
                        Explorar modelos →
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center bg-neutral-50 dark:bg-neutral-800/30 rounded-2xl border border-neutral-200/60 dark:border-neutral-800 space-y-3">
                <p className="text-sm text-neutral-600 dark:text-neutral-300">
                  Explora todos los modelos y soluciones disponibles para {activeCategory.name}.
                </p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleCategoryClick(activeCategory.slug)}
                >
                  Explorar {activeCategory.name}
                </Button>
              </div>
            )}
          </div>

          {/* Banner de Garantía y Asesoría en el pie del Mega Menú */}
          <div className="mt-6 pt-4 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-brand-primary" />
              <span>Equipos con soporte oficial SmartServ y garantía de fábrica.</span>
            </div>
            <Link
              to="/contacto"
              onClick={onClose}
              className="text-neutral-900 dark:text-white font-semibold hover:text-brand-primary transition-colors"
            >
              ¿Dudas técnicas? Asesórate con ingenieros
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
